import NetInfo, { useNetInfo } from '@react-native-community/netinfo';
import { LinearGradient } from 'expo-linear-gradient';
import { NavigationContainer } from '@react-navigation/native';
import { Fragment, useEffect, useState } from 'react';
import { StatusBar, useWindowDimensions, Platform, View } from 'react-native';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { SafeAreaProvider, SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import AppDrawer from './components/common/drawer/AppDrawer';
import CustomModal from './components/common/CustomModal';
import MyAccount from './components/myaccount/MyAccount';
import NavBar from './components/common/navbar/NavBar';
import TopBar from './components/common/topbar/TopBar';
import Notification from './components/common/Notification';
import { NotificationOpenState } from './store/notificationSlice';
import useCustomTheme from './hooks/theme-hook';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Query, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LightBox from './components/common/lightBox/LightBox';
import MainBody from './pages/MainBody';
import ErrorBoundary from 'react-native-error-boundary';
import CustomErrorFallback from './components/common/CustomErrorFallback';
import OffLinePopup from './components/common/OffLinePopup';
import CookiesPopup from './components/myaccount/cookies/CookiesPopup';
import Constants from 'expo-constants';
import BottomSheetProvider from './components/common/bottomSheet/BottomSheetProvider';
import SystemThemeSync from './components/common/SystemThemeSync';
import { rootNavigationRef } from './navigation/rootNavigation';
import { isLockedSessionError } from './helpers/apiErrorHelper';

export default function App() {
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const [currentRouteName, setCurrentRouteName] = useState<string | undefined>();

  const { isConnected } = useNetInfo();

  const routeForBackground = currentRouteName ?? 'entryPage';
  const isAuthBackground =
    routeForBackground === 'entryPage' ||
    routeForBackground === 'login' ||
    routeForBackground === 'referralLogin' ||
    routeForBackground === 'missionReferral' ||
    routeForBackground === 'logout' ||
    routeForBackground === 'useractionreturn' ||
    routeForBackground === 'unsubscribe';
  const isSurveyBackground = routeForBackground === 'survey' || routeForBackground === 'editSurvey';
  const useGradientInsetBackground = isAuthBackground || isSurveyBackground;

  const rootGradient = isAuthBackground
    ? {
        end: { x: 0.1, y: 0.4 },
        colors: ['#022d38', '#034050', '#044c5e', '#04647c', '#43a49e'],
      }
    : isSurveyBackground
      ? {
          start: { x: 0, y: -0.5 },
          end: { x: 1, y: 1 },
          colors: ['#024252', '#035164', '#43A49E'],
        }
      : {
          end: { x: 1.4, y: 0 },
          colors: ['#035164', '#43a49e'],
        };

  const linking = {
    prefixes: ['https://ms.hulk.cool', 'https://ms.hulk.cool', 'lanla://'],
    config: {
      screens: {
        entryPage: '',
        referralLogin: 'referal/:missionId',
        missionReferral: 'missions/referrals/:token',
        survey: 'survey',
        map: 'map',
        validation: 'validation',
        mymissions: 'mymissions',
        myreferences: 'myreferences',
        remotemissions: 'remotemissions',
        useractionreturn: 'useractionreturn',
        unsubscribe: 'unsubscribe',
      },
    },
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error: any) => {
              if (isLockedSessionError(error)) {
                return false;
              }

              return failureCount < 3;
            },
            retryDelay: 1000,
            // staleTime: 60000,
            // cacheTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: false,
            useErrorBoundary: (error: any, query: Query) => {
              // console.log('Error', error);
              // console.log('Error code', error.code);
              // console.log('Error.status', error.status);
              // console.log('Query', query.queryKey);

              if (error.code === 'ERR_CANCELED') {
                console.log('Query cancelled', error.status);
                return false;
              }

              return true;
            },
          },
        },
      }),
  );

  const handleNavigationStateChange = () => {
    const route = rootNavigationRef.getCurrentRoute();
    setCurrentRouteName(route?.name);
  };

  const navigationContent = (
    <Fragment>
      <StatusBar
        animated={false}
        backgroundColor={useGradientInsetBackground ? rootGradient.colors[0] : theme.mapControls}
        barStyle={'light-content'}
        hidden={false}
      />
      <NavigationContainer ref={rootNavigationRef} linking={linking as any} onReady={handleNavigationStateChange} onStateChange={handleNavigationStateChange}>
        {!isConnected && <OffLinePopup />}
        {Platform.OS === 'web' && Constants.expoConfig?.extra?.appUrl === 'https://ms.hulk.cool' && false && <CookiesPopup />}
        <TopBar currentRouteName={currentRouteName} />
        <MainBody />
        {layout.width <= 1200 && !isAuthBackground ? <NavBar /> : <></>}
        <Notification openState={NotificationOpenState.hidden} />
        <MyAccount />
        <AppDrawer />
      </NavigationContainer>
    </Fragment>
  );

  const contentWrapperStyle = {
    flex: 1,
    backgroundColor: isAuthBackground || isSurveyBackground ? 'transparent' : theme.background?.navBar,
  };

  return (
    <Provider store={store}>
      <ErrorBoundary FallbackComponent={CustomErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <GestureHandlerRootView style={{ flex: 1 }}>
            <SafeAreaProvider>
              <BottomSheetProvider>
                <LinearGradient
                  start={(rootGradient as any).start}
                  end={rootGradient.end}
                  style={{ height: '100%', width: '100%' }}
                  colors={rootGradient.colors as any}
                >
                  <Fragment>
                    <SystemThemeSync />
                    {Platform.OS != 'web' && (
                      <SafeAreaView edges={['top']} style={{ flex: 0, backgroundColor: useGradientInsetBackground ? 'transparent' : theme.mapControls }} />
                    )}
                    {Platform.OS === 'web' ? (
                      <View style={contentWrapperStyle}>{navigationContent}</View>
                    ) : (
                      <SafeAreaView edges={['bottom']} style={contentWrapperStyle}>
                        {navigationContent}
                      </SafeAreaView>
                    )}
                    {isSurveyBackground && <SurveyBottomInsetGradient />}
                    <CustomModal />
                    <LightBox />
                  </Fragment>
                </LinearGradient>
              </BottomSheetProvider>
            </SafeAreaProvider>
          </GestureHandlerRootView>
        </QueryClientProvider>
      </ErrorBoundary>
    </Provider>
  );
}

const SurveyBottomInsetGradient = () => {
  const insets = useSafeAreaInsets();

  if (Platform.OS === 'web' || insets.bottom <= 0) {
    return null;
  }

  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: insets.bottom,
      }}
    >
      <LinearGradient colors={['#024252', '#035164', '#43A49E']} start={{ x: 0, y: -0.5 }} end={{ x: 1, y: 1 }} style={{ flex: 1 }} />
    </View>
  );
};
